import React from 'react';
import { Filter } from '../types';

// Default filter options
export const DEFAULT_FILTERS: Filter[] = [
  { label: 'Restaurants', icon: 'restaurant', preference: 'restaurant' },
  { label: 'Coffee', icon: 'coffee', preference: 'coffee' },
  { label: 'Hotels', icon: 'hotel', preference: 'hotel' },
  { label: 'Attractions', icon: 'attractions', preference: 'attraction' },
  { label: 'Gas', icon: 'local_gas_station', preference: 'gas_station' },
  { label: 'Parking', icon: 'local_parking', preference: 'parking' },
  { label: 'Shopping', icon: 'shopping_bag', preference: 'shopping' },
  { label: 'Parks', icon: 'park', preference: 'park' },
];

interface FilterChipsProps {
  filters?: Filter[];
  selectedFilters?: string[];
  onFilterClick: (preference: string) => void;
  onFilterToggle?: (preference: string, selected: boolean) => void;
}

export const FilterChips: React.FC<FilterChipsProps> = ({
  filters = DEFAULT_FILTERS,
  selectedFilters = [],
  onFilterClick,
  onFilterToggle
}) => {
  const handleClick = (filter: Filter) => {
    if (onFilterToggle) {
      const isSelected = selectedFilters.includes(filter.preference);
      onFilterToggle(filter.preference, !isSelected);
    }
    onFilterClick(filter.preference);
  };

  return (
    <div className="overflow-x-auto no-scrollbar">
      <div className="flex space-x-2 px-4 py-1">
        {filters.map((filter) => {
          const isSelected = selectedFilters.includes(filter.preference);
          return (
            <button
              key={filter.preference}
              onClick={() => handleClick(filter)}
              className={`flex-shrink-0 rounded-full px-4 py-2 flex items-center space-x-1.5 transition-all touch-feedback ${
                isSelected
                  ? 'bg-blue-600 text-white shadow-sm border border-blue-600'
                  : 'bg-white border border-gray-200 shadow-sm text-gray-700 active:bg-gray-100'
              }`}
            >
              <span className={`material-symbols-outlined text-[18px] ${isSelected ? 'text-white' : 'text-gray-600'}`}>
                {filter.icon}
              </span>
              <span className="text-sm font-medium">{filter.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Single chip component for reuse
interface ChipProps {
  label: string;
  icon?: string;
  selected?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
}

export const Chip: React.FC<ChipProps> = ({
  label,
  icon,
  selected = false,
  onClick,
  onRemove
}) => {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center rounded-full px-3 py-1.5 text-sm font-medium transition-all touch-feedback ${
        selected
          ? 'bg-blue-600 text-white'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {icon && (
        <span className={`material-symbols-outlined text-[16px] mr-1 ${selected ? 'text-white' : 'text-gray-500'}`}>
          {icon}
        </span>
      )}
      <span>{label}</span>
      {onRemove && (
        <span
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className={`ml-1.5 material-symbols-outlined text-[16px] cursor-pointer ${
            selected ? 'text-blue-200 hover:text-white' : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          close
        </span>
      )}
    </button>
  );
};
